import { Check, Loader2, MapPin, Music, Pause, Play, RotateCcw, Shuffle, Upload } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { musicPreviewUrl, updateMusicTrackOffset, uploadMusicTrack, type MusicTrack } from "../api";
import { prettifyTrackName } from "../lib/music";
import { useIsMobile } from "../lib/useIsMobile";

/** Format a seconds-float as "m:ss" (or "h:mm:ss" for long tracks).
 *  Rounded to whole seconds — the scrubber doesn't need sub-second
 *  precision in the operator's mental model, and "1:42.3" reads as
 *  noise vs the clean "1:42" we want to display. */
function formatTime(t: number): string {
  if (!Number.isFinite(t) || t < 0) return "0:00";
  const total = Math.round(t);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Tolerance (seconds) within which the current playback position is
 *  treated as "still at the pinned default" — the Pin button stays
 *  hidden so the operator isn't tempted to re-pin essentially the
 *  same moment. */
const PIN_TOLERANCE_SEC = 1.0;

/**
 * Background-music picker. Scrollable radio list of tracks with an
 * inline scrubber + "pin moment as default" affordance for the active
 * preview, and an Upload button to add new tracks without leaving the
 * form. value="" maps to "Let Claude pick" (legacy auto-pick path
 * preserved so a missing catalog or operator preference doesn't break
 * the render). Used in BOTH NewJob (create flow) and JobDetail (re-cut
 * flow) — keeping one component means the UX and the pinning model
 * stay identical wherever music is chosen.
 *
 * Props are deliberately controlled — the parent owns `selected` and
 * the `tracks` list so a re-render outside the picker (volume change,
 * sibling form fields) doesn't lose state. `onTracksChange` is fired
 * after a pin or upload so the parent can refresh its cached copy of
 * the catalog.
 */
export function MusicPicker({
  tracks,
  selected,
  onChange,
  onTracksChange,
  onReshuffle,
  loadError,
}: {
  tracks: MusicTrack[] | null;
  selected: string;
  onChange: (track: string) => void;
  onTracksChange: (tracks: MusicTrack[]) => void;
  onReshuffle?: () => void;
  loadError: string | null;
}): React.JSX.Element {
  // Single shared <audio> — at most one track plays at a time. Holding
  // it in a ref keeps the element across re-renders, so seeking /
  // playback state survive selection changes and shuffles.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  const [loadingTrack, setLoadingTrack] = useState<string | null>(null);
  // Scrubber state — currentTime advances from the <audio> element's
  // timeupdate event while the active preview plays. Frozen on pause so
  // the operator can read the exact moment before pinning.
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Optimistic-update spinner state for the pin button; null when no
  // PATCH is in flight.
  const [pinningFilename, setPinningFilename] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isMobile = useIsMobile();
  const trackByName = (name: string): MusicTrack | undefined =>
    (tracks ?? []).find((t) => t.filename === name);

  // Stop on unmount so navigating away of the parent screen silences
  // playback.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, []);

  function togglePreview(filename: string): void {
    if (!filename) return; // "Auto" row has no audio to preview.
    let a = audioRef.current;
    if (!a) {
      a = new Audio();
      a.preload = "auto";
      a.onended = (): void => setPlayingTrack(null);
      a.onpause = (): void => {
        // External pause (e.g. tab switch) — keep UI in sync.
        if (a && a.paused) setPlayingTrack((cur) => (cur === filename ? null : cur));
      };
      a.onerror = (): void => {
        setPlayingTrack(null);
        setLoadingTrack(null);
      };
      // ontimeupdate fires ~4×/s in most browsers — smooth enough for
      // a scrubber while keeping React render churn bounded.
      a.ontimeupdate = (): void => {
        if (a) setCurrentTime(a.currentTime);
      };
      a.ondurationchange = (): void => {
        if (a && Number.isFinite(a.duration)) setDuration(a.duration);
      };
      audioRef.current = a;
    }
    if (playingTrack === filename) {
      a.pause();
      setPlayingTrack(null);
      return;
    }
    // Switching tracks — stop the previous one cleanly before
    // pointing src at the new file, otherwise some browsers fire
    // a stray onended for the abandoned source.
    a.pause();
    a.currentTime = 0;
    setCurrentTime(0);
    setDuration(0);
    a.src = musicPreviewUrl(filename);
    // Auto-seek to the pinned default once metadata is available —
    // that's what the render will use, so auditioning should start
    // there by default. The "↺ from start" button lets the operator
    // listen from t=0 when they want to scout a NEW pin point.
    const offsetOnPlay = trackByName(filename)?.startOffsetSec ?? 0;
    a.onloadedmetadata = (): void => {
      if (!a) return;
      if (Number.isFinite(a.duration)) setDuration(a.duration);
      if (offsetOnPlay > 0 && offsetOnPlay < a.duration) {
        a.currentTime = offsetOnPlay;
        setCurrentTime(offsetOnPlay);
      }
    };
    setLoadingTrack(filename);
    a.play().then(() => {
      setLoadingTrack((cur) => (cur === filename ? null : cur));
      setPlayingTrack(filename);
    }).catch(() => {
      setLoadingTrack(null);
      setPlayingTrack(null);
    });
  }

  function seekTo(t: number): void {
    const a = audioRef.current;
    if (!a) return;
    const clamped = Math.max(0, Math.min(t, duration || t));
    a.currentTime = clamped;
    setCurrentTime(clamped);
  }

  /** Commit the current playback position as the track's pinned
   *  default. Optimistic — the row title updates instantly; on error
   *  we revert and surface a banner so the operator knows the server
   *  rejected the write. */
  async function pinHere(filename: string, offsetSec: number): Promise<void> {
    if (!tracks) return;
    setPinError(null);
    setPinningFilename(filename);
    const prev = tracks;
    const optimistic = tracks.map((t) =>
      t.filename === filename ? { ...t, startOffsetSec: offsetSec } : t,
    );
    onTracksChange(optimistic);
    try {
      const updated = await updateMusicTrackOffset(filename, offsetSec);
      onTracksChange(optimistic.map((t) => (t.filename === filename ? updated : t)));
    } catch (err) {
      onTracksChange(prev);
      setPinError(err instanceof Error ? err.message : String(err));
    } finally {
      setPinningFilename(null);
    }
  }

  /** Upload a local audio file, insert it alphabetically into the
   *  catalog, and auto-select it so the operator can immediately
   *  scrub-and-pin. */
  async function handleFileSelected(file: File): Promise<void> {
    setUploadError(null);
    setUploading(true);
    try {
      const newTrack = await uploadMusicTrack(file);
      const merged = [...(tracks ?? []), newTrack].sort((a, b) =>
        a.filename.localeCompare(b.filename),
      );
      onTracksChange(merged);
      onChange(newTrack.filename);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      // Clear the input so the same file can be re-selected if the
      // upload failed and the operator wants to retry without picking
      // a different file first.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  if (loadError) {
    return (
      <div style={{ fontSize: 12, color: "var(--ql-danger)" }}>
        Couldn't load music catalog: {loadError}. The render will fall back to
        Claude's automatic pick.
      </div>
    );
  }
  if (tracks === null) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ql-fg-muted)" }}>
        <Loader2 size={13} className="ql-spin" /> Loading music catalog…
      </div>
    );
  }

  // "Auto" row prepended so the operator can explicitly opt out of
  // a fixed pick without scrolling. value="" maps to the backend's
  // "no musicTrack → Claude pick" branch.
  const rows: Array<{ value: string; label: string; sub: string; auto: boolean; startOffsetSec: number }> = [
    {
      value: "",
      label: "Pick for me",
      sub: "Automatic — the pipeline reads the transcript and chooses",
      auto: true,
      startOffsetSec: 0,
    },
    ...tracks.map((t) => ({
      value: t.filename,
      label: prettifyTrackName(t.filename),
      sub: t.filename,
      auto: false,
      startOffsetSec: t.startOffsetSec,
    })),
  ];

  const headerBtnStyle: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.10)",
    background: "rgba(255,255,255,.04)",
    color: "var(--ql-fg-muted)",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0, marginBottom: 10 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ql-fg-muted)" }}>
          <Music size={13} strokeWidth={2} />
          <span>
            {selected
              ? <>Selected: <strong style={{ color: "var(--ql-fg)" }}>{prettifyTrackName(selected)}</strong></>
              : <>Default: <strong style={{ color: "var(--ql-fg)" }}>picked for you</strong></>}
          </span>
        </div>
        <div style={{ display: "inline-flex", gap: 8 }}>
          {/* Native <label> → <input type=file> activation. This used to
              be a <button onClick={() => fileInputRef.current?.click()}>
              over a `display: none` input. When that programmatic click
              didn't reach the input, the optional chaining swallowed it
              and the operator got NOTHING — no picker, no error, no
              request (reported 2026-08-31: "I click upload and nothing
              happens, like if I did not click on it"). The browser opens
              the picker off the label itself, so there is no JS left in
              that path to fail silently. Keep it that way. */}
          <label
            title="Upload a new track (.mp3 / .m4a / .wav, ≤25 MiB)"
            style={{
              ...headerBtnStyle,
              position: "relative",
              opacity: uploading ? 0.6 : 1,
              // A <label> has no disabled attribute — take it out of the
              // pointer path while an upload is already in flight.
              pointerEvents: uploading ? "none" : "auto",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFileSelected(f);
              }}
              // Visually hidden but still rendered and hit-testable.
              // `display: none` is precisely the state a browser may
              // refuse to activate, which is what we're getting away from.
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                opacity: 0,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            />
            {uploading ? <Loader2 size={12} className="ql-spin" /> : <Upload size={12} strokeWidth={2} />} Upload
          </label>
          {onReshuffle && tracks.length > 0 && (
            <button
              type="button"
              onClick={onReshuffle}
              title="Re-roll the random pick"
              style={headerBtnStyle}
            >
              <Shuffle size={12} strokeWidth={2} /> Shuffle
            </button>
          )}
        </div>
      </div>

      {uploadError && (
        <div style={{ fontSize: 12, color: "var(--ql-danger)", marginBottom: 8 }}>
          Upload failed: {uploadError}
        </div>
      )}
      {pinError && (
        <div style={{ fontSize: 12, color: "var(--ql-danger)", marginBottom: 8 }}>
          Couldn't save start offset: {pinError}
        </div>
      )}

      {tracks.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>
          No music tracks yet. Click <strong>Upload</strong> above to add one,
          or the render will fall back to Claude's pick (or silence if Claude
          can't pick either).
        </div>
      ) : (
      <div
        role="radiogroup"
        aria-label="Background music track"
        style={{
          maxHeight: 340,
          overflowY: "auto",
          border: "1px solid rgba(255,255,255,.06)",
          borderRadius: 12,
          background: "rgba(0,0,0,.20)",
        }}
      >
        {rows.map((row) => {
          const isSelected = selected === row.value;
          const isPlaying = playingTrack === row.value;
          const pinnedOffset = row.startOffsetSec;
          const showPinButton =
            isPlaying
            && duration > 0
            && Math.abs(currentTime - pinnedOffset) > PIN_TOLERANCE_SEC;
          return (
            <div
              key={row.value || "__auto__"}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => onChange(row.value)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  onChange(row.value);
                }
              }}
              style={{
                display: "block",
                padding: "10px 14px",
                cursor: "pointer",
                borderBottom: "1px solid rgba(255,255,255,.04)",
                background: isSelected ? "rgba(255,213,0,.08)" : "transparent",
                transition: "background .12s var(--ql-ease)",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,.03)";
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* Custom radio circle — checkbox-like with a yellow tick when selected. */}
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    border: `1.5px solid ${isSelected ? "var(--ql-yellow)" : "rgba(255,255,255,.15)"}`,
                    background: isSelected ? "var(--ql-yellow)" : "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all .12s var(--ql-ease)",
                  }}
                >
                  {isSelected && <Check size={11} strokeWidth={3} color="#000" />}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "var(--ql-fg)" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</span>
                    {!row.auto && pinnedOffset > 0 && (
                      <span
                        title={`Default starts at ${formatTime(pinnedOffset)} — click Play to audition from there.`}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--ql-yellow)",
                          background: "rgba(255,213,0,.10)",
                          border: "1px solid rgba(255,213,0,.25)",
                          padding: "1px 6px",
                          borderRadius: 999,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        <MapPin size={10} strokeWidth={2.4} style={{ verticalAlign: "-1px", marginRight: 2 }} />
                        {formatTime(pinnedOffset)}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--ql-fg-muted)",
                      fontFamily: row.auto ? "inherit" : "var(--ql-font-mono)",
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.sub}
                  </div>
                </div>
                {!row.auto && (
                  <button
                    type="button"
                    aria-label={isPlaying ? `Pause ${row.label}` : `Play ${row.label}`}
                    title={isPlaying ? "Pause preview" : "Play preview (starts from pinned default)"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePreview(row.value);
                      onChange(row.value);
                    }}
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(255,255,255,.10)",
                      background: isPlaying ? "var(--ql-yellow)" : "rgba(255,255,255,.04)",
                      color: isPlaying ? "#000" : "var(--ql-fg)",
                      cursor: "pointer",
                      transition: "all .12s var(--ql-ease)",
                    }}
                  >
                    {loadingTrack === row.value
                      ? <Loader2 size={14} className="ql-spin" />
                      : isPlaying
                        ? <Pause size={14} strokeWidth={2.2} />
                        : <Play size={14} strokeWidth={2.2} style={{ marginLeft: 2 }} />}
                  </button>
                )}
              </div>

              {/* Inline scrubber — visible only while THIS row's audio
                  is the live preview. The yellow tick on the bar shows
                  where the render will start by default; the Pin button
                  surfaces when the scrubber has drifted >1s away from
                  it. Click events stopPropagation so the row's radio
                  selection doesn't toggle while the operator is
                  scrubbing. */}
              {isPlaying && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ marginTop: 10, paddingLeft: 30, display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)", minWidth: 42, textAlign: "right" }}>
                      {formatTime(currentTime)}
                    </span>
                    <div style={{ position: "relative", flex: 1, height: 18, display: "flex", alignItems: "center" }}>
                      <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        step={0.1}
                        value={Math.min(currentTime, duration || currentTime)}
                        onChange={(e) => seekTo(Number(e.target.value))}
                        aria-label="Seek within preview"
                        style={{
                          width: "100%",
                          accentColor: "var(--ql-yellow)",
                          cursor: "pointer",
                        }}
                      />
                      {duration > 0 && pinnedOffset > 0 && pinnedOffset < duration && (
                        <div
                          aria-hidden="true"
                          title={`Default starts at ${formatTime(pinnedOffset)}`}
                          style={{
                            position: "absolute",
                            left: `${(pinnedOffset / duration) * 100}%`,
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 2,
                            height: 18,
                            background: "var(--ql-yellow)",
                            borderRadius: 1,
                            pointerEvents: "none",
                            boxShadow: "0 0 0 1px rgba(0,0,0,.45)",
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)", minWidth: 42 }}>
                      {formatTime(duration)}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); seekTo(0); }}
                      title="Restart preview from 0:00"
                      style={headerBtnStyle}
                    >
                      <RotateCcw size={11} strokeWidth={2} /> From start
                    </button>
                    {pinnedOffset > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); seekTo(pinnedOffset); }}
                        title={`Jump to the current default (${formatTime(pinnedOffset)})`}
                        style={headerBtnStyle}
                      >
                        <MapPin size={11} strokeWidth={2} /> Jump to default
                      </button>
                    )}
                    {showPinButton && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); void pinHere(row.value, currentTime); }}
                        disabled={pinningFilename === row.value}
                        title={`Set ${formatTime(currentTime)} as this track's default start moment.`}
                        style={{
                          ...headerBtnStyle,
                          color: "#000",
                          background: "var(--ql-yellow)",
                          border: "1px solid var(--ql-yellow)",
                          opacity: pinningFilename === row.value ? 0.7 : 1,
                        }}
                      >
                        {pinningFilename === row.value
                          ? <Loader2 size={11} className="ql-spin" />
                          : <MapPin size={11} strokeWidth={2.2} />}
                        {pinnedOffset > 0
                          ? <>Pin {formatTime(currentTime)} as new default</>
                          : <>Pin {formatTime(currentTime)} as default</>}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
      <div style={{ marginTop: 8, fontSize: 11, color: "var(--ql-fg-muted)" }}>
        Click Play to audition. The render starts from the yellow mark on the
        scrubber — scrub somewhere else and hit <strong>Pin</strong> to make
        that the new default for the track.
      </div>
    </div>
  );
}
